import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Calendar, Clock, User, Phone, Mail, 
  ChevronRight, X, AlertCircle, CheckCircle,
  LogOut, Settings, FileText
} from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Portal = () => {
  const navigate = useNavigate();
  const { user, token, logout, isAuthenticated, loading: authLoading } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  const fetchAppointments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/appointments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAppointments(response.data);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (token) {
      fetchAppointments();
    }
  }, [token, fetchAppointments]);

  const handleCancelClick = (appointment) => {
    setSelectedAppointment(appointment);
    setShowCancelDialog(true);
  };

  const handleCancelConfirm = async () => {
    if (!selectedAppointment) return;
    
    setCancellingId(selectedAppointment.id);
    
    try {
      await axios.patch(
        `${API}/appointments/${selectedAppointment.id}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Appointment cancelled successfully');
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to cancel appointment');
    } finally {
      setCancellingId(null);
      setShowCancelDialog(false);
      setSelectedAppointment(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return <Badge className="bg-green-100 text-green-700 border-green-200">Confirmed</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-700 border-red-200">Cancelled</Badge>;
      case 'completed':
        return <Badge className="bg-blue-100 text-blue-700 border-blue-200">Completed</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Pending</Badge>;
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#4A6FA5] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const upcomingAppointments = appointments.filter(
    apt => apt.status === 'pending' || apt.status === 'confirmed'
  );
  const pastAppointments = appointments.filter(
    apt => apt.status === 'completed' || apt.status === 'cancelled'
  );

  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="patient-portal">
      {/* Header */}
      <div className="bg-[#1E2A4A] py-8">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="font-['Cormorant_Garamond'] text-3xl font-medium text-white">
                Welcome back, {user?.name?.split(' ')[0]}
              </h1>
              <p className="text-[#E8EEF5]/60 mt-1">
                Manage your appointments and health records
              </p>
            </div>
            <div className="flex gap-3">
              <Link to="/book-appointment">
                <Button className="bg-[#4A6FA5] hover:bg-[#3d5d8a] text-white">
                  <Calendar className="w-4 h-4 mr-2" />
                  New Appointment
                </Button>
              </Link>
              <Button 
                variant="outline" 
                onClick={handleLogout}
                className="border-white/20 text-white hover:bg-white/10"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container-custom py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Upcoming Appointments */}
            <section>
              <h2 className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[#1E2A4A] mb-4">
                Upcoming Appointments
              </h2>
              
              {loading ? (
                <div className="bg-white rounded-2xl p-8 text-center">
                  <div className="w-8 h-8 border-2 border-[#4A6FA5] border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : upcomingAppointments.length === 0 ? (
                <div className="bg-white rounded-2xl p-8 text-center">
                  <Calendar className="w-12 h-12 text-[#E8EEF5] mx-auto mb-4" />
                  <p className="text-[#7A8FA0] mb-4">No upcoming appointments</p>
                  <Link to="/book-appointment">
                    <Button className="bg-[#4A6FA5] hover:bg-[#1E2A4A] text-white">
                      Book an Appointment
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {upcomingAppointments.map((apt) => (
                    <motion.div
                      key={apt.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white rounded-2xl p-6 shadow-sm"
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 bg-[#4A6FA5]/10 rounded-xl flex flex-col items-center justify-center">
                            <span className="text-lg font-semibold text-[#4A6FA5]">
                              {format(new Date(apt.date), 'd')}
                            </span>
                            <span className="text-[10px] uppercase text-[#7A8FA0]">
                              {format(new Date(apt.date), 'MMM')}
                            </span>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              {getStatusBadge(apt.status)}
                            </div>
                            <p className="text-sm font-medium text-[#1E2A4A]">
                              {format(new Date(apt.date), 'EEEE, MMMM d, yyyy')}
                            </p>
                            <p className="text-sm text-[#4A6FA5]">{apt.time_slot}</p>
                            <p className="text-sm text-[#7A8FA0] mt-1">{apt.concern}</p>
                          </div>
                        </div>
                        {apt.status !== 'cancelled' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCancelClick(apt)}
                            disabled={cancellingId === apt.id}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            data-testid={`cancel-apt-${apt.id}`}
                          >
                            {cancellingId === apt.id ? 'Cancelling...' : 'Cancel'}
                          </Button>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </section>

            {/* Past Appointments */}
            {pastAppointments.length > 0 && (
              <section>
                <h2 className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[#1E2A4A] mb-4">
                  Past Appointments
                </h2>
                <div className="space-y-4">
                  {pastAppointments.map((apt) => (
                    <div
                      key={apt.id}
                      className="bg-white/50 rounded-2xl p-6"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 bg-[#F5F7FA] rounded-xl flex flex-col items-center justify-center">
                          <span className="text-lg font-semibold text-[#7A8FA0]">
                            {format(new Date(apt.date), 'd')}
                          </span>
                          <span className="text-[10px] uppercase text-[#7A8FA0]">
                            {format(new Date(apt.date), 'MMM')}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {getStatusBadge(apt.status)}
                          </div>
                          <p className="text-sm font-medium text-[#7A8FA0]">
                            {format(new Date(apt.date), 'MMMM d, yyyy')} at {apt.time_slot}
                          </p>
                          <p className="text-sm text-[#7A8FA0] mt-1">{apt.concern}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Profile Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="text-xs uppercase tracking-wider text-[#7A8FA0] mb-4">Your Profile</h3>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#1E2A4A] to-[#4A6FA5] flex items-center justify-center text-white font-['Cormorant_Garamond'] text-2xl">
                  {user?.name?.charAt(0)}
                </div>
                <div>
                  <p className="font-semibold text-[#1E2A4A]">{user?.name}</p>
                  <p className="text-sm text-[#7A8FA0]">Patient</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="w-4 h-4 text-[#4A6FA5]" />
                  <span className="text-[#5e7080]">{user?.email}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Phone className="w-4 h-4 text-[#4A6FA5]" />
                  <span className="text-[#5e7080]">{user?.phone}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Calendar className="w-4 h-4 text-[#4A6FA5]" />
                  <span className="text-[#5e7080]">
                    Member since {user?.created_at ? format(new Date(user.created_at), 'MMM yyyy') : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="text-xs uppercase tracking-wider text-[#7A8FA0] mb-4">Quick Links</h3>
              <div className="space-y-2">
                <Link 
                  to="/book-appointment"
                  className="flex items-center justify-between p-3 rounded-lg bg-[#F5F7FA] hover:bg-[#E8EEF5] transition-colors"
                >
                  <span className="flex items-center gap-3 text-sm text-[#1E2A4A]">
                    <Calendar className="w-4 h-4 text-[#4A6FA5]" />
                    Book Appointment
                  </span>
                  <ChevronRight className="w-4 h-4 text-[#7A8FA0]" />
                </Link>
                <Link 
                  to="/blog"
                  className="flex items-center justify-between p-3 rounded-lg bg-[#F5F7FA] hover:bg-[#E8EEF5] transition-colors"
                >
                  <span className="flex items-center gap-3 text-sm text-[#1E2A4A]">
                    <FileText className="w-4 h-4 text-[#4A6FA5]" />
                    Health Articles
                  </span>
                  <ChevronRight className="w-4 h-4 text-[#7A8FA0]" />
                </Link>
                <Link 
                  to="/#contact"
                  className="flex items-center justify-between p-3 rounded-lg bg-[#F5F7FA] hover:bg-[#E8EEF5] transition-colors"
                >
                  <span className="flex items-center gap-3 text-sm text-[#1E2A4A]">
                    <Phone className="w-4 h-4 text-[#4A6FA5]" />
                    Contact Clinic
                  </span>
                  <ChevronRight className="w-4 h-4 text-[#7A8FA0]" />
                </Link>
              </div>
            </div>

            {/* Clinic Info */}
            <div className="bg-[#1E2A4A] rounded-2xl p-6 text-center">
              <h4 className="font-['Cormorant_Garamond'] text-lg text-[#E8EEF5] mb-2">
                Need Help?
              </h4>
              <p className="text-xs text-[#E8EEF5]/50 mb-4">
                Contact us for any queries
              </p>
              <a href="tel:+917043543985">
                <Button className="w-full bg-[#4A6FA5] hover:bg-[#3d5d8a] text-white">
                  <Phone className="w-4 h-4 mr-2" />
                  +91-7043543985
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Appointment?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel your appointment on{' '}
              {selectedAppointment && format(new Date(selectedAppointment.date), 'MMMM d, yyyy')} at{' '}
              {selectedAppointment?.time_slot}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Appointment</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCancelConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Yes, Cancel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Portal;
